import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Available currencies with their symbols and formatting
export const CURRENCIES = {
  SAR: {
    code: 'SAR',
    name: 'Saudi Riyal',
    nameAr: 'الريال السعودي',
    symbol: 'SAR',
    symbolAr: '﷼',
    symbolPosition: 'after', // 'before' or 'after'
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  USD: {
    code: 'USD',
    name: 'US Dollar',
    nameAr: 'الدولار الأمريكي',
    symbol: '$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  EUR: {
    code: 'EUR',
    name: 'Euro',
    nameAr: 'اليورو',
    symbol: '€',
    symbolAr: 'يورو',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  GBP: {
    code: 'GBP',
    name: 'British Pound',
    nameAr: 'الجنيه الإسترليني',
    symbol: '£',
    symbolAr: 'جنيه',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  AED: {
    code: 'AED',
    name: 'UAE Dirham',
    nameAr: 'الدرهم الإماراتي',
    symbol: 'AED',
    symbolAr: 'درهم',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KWD: {
    code: 'KWD',
    name: 'Kuwaiti Dinar',
    nameAr: 'الدينار الكويتي',
    symbol: 'KWD',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  QAR: {
    code: 'QAR',
    name: 'Qatari Riyal',
    nameAr: 'الريال القطري',
    symbol: 'QAR',
    symbolAr: 'ريال',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BHD: {
    code: 'BHD',
    name: 'Bahraini Dinar',
    nameAr: 'الدينار البحريني',
    symbol: 'BHD',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  OMR: {
    code: 'OMR',
    name: 'Omani Rial',
    nameAr: 'الريال العماني',
    symbol: 'OMR',
    symbolAr: 'ريال',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  JOD: {
    code: 'JOD',
    name: 'Jordanian Dinar',
    nameAr: 'الدينار الأردني',
    symbol: 'JOD',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  EGP: {
    code: 'EGP',
    name: 'Egyptian Pound',
    nameAr: 'الجنيه المصري',
    symbol: 'EGP',
    symbolAr: 'جنيه',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // European Currencies
  CHF: {
    code: 'CHF',
    name: 'Swiss Franc',
    nameAr: 'الفرنك السويسري',
    symbol: 'CHF',
    symbolAr: 'فرنك',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SEK: {
    code: 'SEK',
    name: 'Swedish Krona',
    nameAr: 'الكرونة السويدية',
    symbol: 'kr',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  NOK: {
    code: 'NOK',
    name: 'Norwegian Krone',
    nameAr: 'الكرونة النرويجية',
    symbol: 'kr',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  DKK: {
    code: 'DKK',
    name: 'Danish Krone',
    nameAr: 'الكرونة الدنماركية',
    symbol: 'kr',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PLN: {
    code: 'PLN',
    name: 'Polish Zloty',
    nameAr: 'الزلوتي البولندي',
    symbol: 'zł',
    symbolAr: 'زلوتي',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  CZK: {
    code: 'CZK',
    name: 'Czech Koruna',
    nameAr: 'الكرونة التشيكية',
    symbol: 'Kč',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  HUF: {
    code: 'HUF',
    name: 'Hungarian Forint',
    nameAr: 'الفورنت المجري',
    symbol: 'Ft',
    symbolAr: 'فورنت',
    symbolPosition: 'after',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  RON: {
    code: 'RON',
    name: 'Romanian Leu',
    nameAr: 'الليو الروماني',
    symbol: 'lei',
    symbolAr: 'ليو',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BGN: {
    code: 'BGN',
    name: 'Bulgarian Lev',
    nameAr: 'الليف البلغاري',
    symbol: 'лв',
    symbolAr: 'ليف',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  HRK: {
    code: 'HRK',
    name: 'Croatian Kuna',
    nameAr: 'الكونا الكرواتية',
    symbol: 'kn',
    symbolAr: 'كونا',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  RSD: {
    code: 'RSD',
    name: 'Serbian Dinar',
    nameAr: 'الدينار الصربي',
    symbol: 'дин',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BAM: {
    code: 'BAM',
    name: 'Bosnia-Herzegovina Mark',
    nameAr: 'المارك البوسني',
    symbol: 'KM',
    symbolAr: 'مارك',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MKD: {
    code: 'MKD',
    name: 'Macedonian Denar',
    nameAr: 'الدينار المقدوني',
    symbol: 'ден',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  ALL: {
    code: 'ALL',
    name: 'Albanian Lek',
    nameAr: 'الليك الألباني',
    symbol: 'L',
    symbolAr: 'ليك',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MDL: {
    code: 'MDL',
    name: 'Moldovan Leu',
    nameAr: 'الليو المولدوفي',
    symbol: 'L',
    symbolAr: 'ليو',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  UAH: {
    code: 'UAH',
    name: 'Ukrainian Hryvnia',
    nameAr: 'الهريفنيا الأوكرانية',
    symbol: '₴',
    symbolAr: 'هريفنيا',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  RUB: {
    code: 'RUB',
    name: 'Russian Ruble',
    nameAr: 'الروبل الروسي',
    symbol: '₽',
    symbolAr: 'روبل',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BYN: {
    code: 'BYN',
    name: 'Belarusian Ruble',
    nameAr: 'الروبل البيلاروسي',
    symbol: 'Br',
    symbolAr: 'روبل',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  GEL: {
    code: 'GEL',
    name: 'Georgian Lari',
    nameAr: 'اللاري الجورجي',
    symbol: '₾',
    symbolAr: 'لاري',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  AMD: {
    code: 'AMD',
    name: 'Armenian Dram',
    nameAr: 'الدرام الأرمني',
    symbol: '֏',
    symbolAr: 'درام',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  AZN: {
    code: 'AZN',
    name: 'Azerbaijani Manat',
    nameAr: 'المانات الأذربيجاني',
    symbol: '₼',
    symbolAr: 'مانات',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KZT: {
    code: 'KZT',
    name: 'Kazakhstani Tenge',
    nameAr: 'التنغي الكازاخستاني',
    symbol: '₸',
    symbolAr: 'تنغي',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KGS: {
    code: 'KGS',
    name: 'Kyrgyzstani Som',
    nameAr: 'السوم القيرغيزستاني',
    symbol: 'с',
    symbolAr: 'سوم',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  TJS: {
    code: 'TJS',
    name: 'Tajikistani Somoni',
    nameAr: 'السوموني الطاجيكستاني',
    symbol: 'SM',
    symbolAr: 'سوموني',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  UZS: {
    code: 'UZS',
    name: 'Uzbekistani Som',
    nameAr: 'السوم الأوزبكستاني',
    symbol: 'сўм',
    symbolAr: 'سوم',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  TMT: {
    code: 'TMT',
    name: 'Turkmenistani Manat',
    nameAr: 'المانات التركمانستاني',
    symbol: 'T',
    symbolAr: 'مانات',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  AFN: {
    code: 'AFN',
    name: 'Afghan Afghani',
    nameAr: 'الأفغاني الأفغاني',
    symbol: '؋',
    symbolAr: 'أفغاني',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // Asian Currencies
  JPY: {
    code: 'JPY',
    name: 'Japanese Yen',
    nameAr: 'الين الياباني',
    symbol: '¥',
    symbolAr: 'ين',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KRW: {
    code: 'KRW',
    name: 'South Korean Won',
    nameAr: 'الوون الكوري الجنوبي',
    symbol: '₩',
    symbolAr: 'وون',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  CNY: {
    code: 'CNY',
    name: 'Chinese Yuan',
    nameAr: 'اليوان الصيني',
    symbol: '¥',
    symbolAr: 'يوان',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  INR: {
    code: 'INR',
    name: 'Indian Rupee',
    nameAr: 'الروبية الهندية',
    symbol: '₹',
    symbolAr: 'روبية',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SGD: {
    code: 'SGD',
    name: 'Singapore Dollar',
    nameAr: 'الدولار السنغافوري',
    symbol: 'S$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MYR: {
    code: 'MYR',
    name: 'Malaysian Ringgit',
    nameAr: 'الرينغيت الماليزي',
    symbol: 'RM',
    symbolAr: 'رينغيت',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  THB: {
    code: 'THB',
    name: 'Thai Baht',
    nameAr: 'الباهت التايلندي',
    symbol: '฿',
    symbolAr: 'باهت',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PHP: {
    code: 'PHP',
    name: 'Philippine Peso',
    nameAr: 'البيزو الفلبيني',
    symbol: '₱',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  IDR: {
    code: 'IDR',
    name: 'Indonesian Rupiah',
    nameAr: 'الروبية الإندونيسية',
    symbol: 'Rp',
    symbolAr: 'روبية',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  VND: {
    code: 'VND',
    name: 'Vietnamese Dong',
    nameAr: 'الدونغ الفيتنامي',
    symbol: '₫',
    symbolAr: 'دونغ',
    symbolPosition: 'after',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MMK: {
    code: 'MMK',
    name: 'Myanmar Kyat',
    nameAr: 'الكيات الميانماري',
    symbol: 'K',
    symbolAr: 'كيات',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KHR: {
    code: 'KHR',
    name: 'Cambodian Riel',
    nameAr: 'الريال الكمبودي',
    symbol: '៛',
    symbolAr: 'ريال',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  LAK: {
    code: 'LAK',
    name: 'Lao Kip',
    nameAr: 'الكيب اللاوسي',
    symbol: '₭',
    symbolAr: 'كيب',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BDT: {
    code: 'BDT',
    name: 'Bangladeshi Taka',
    nameAr: 'التاكا البنغلاديشي',
    symbol: '৳',
    symbolAr: 'تاكا',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  LKR: {
    code: 'LKR',
    name: 'Sri Lankan Rupee',
    nameAr: 'الروبية السريلانكية',
    symbol: 'Rs',
    symbolAr: 'روبية',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  NPR: {
    code: 'NPR',
    name: 'Nepalese Rupee',
    nameAr: 'الروبية النيبالية',
    symbol: 'Rs',
    symbolAr: 'روبية',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PKR: {
    code: 'PKR',
    name: 'Pakistani Rupee',
    nameAr: 'الروبية الباكستانية',
    symbol: 'Rs',
    symbolAr: 'روبية',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  IRR: {
    code: 'IRR',
    name: 'Iranian Rial',
    nameAr: 'الريال الإيراني',
    symbol: 'IRR',
    symbolAr: 'ريال',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  IQD: {
    code: 'IQD',
    name: 'Iraqi Dinar',
    nameAr: 'الدينار العراقي',
    symbol: 'IQD',
    symbolAr: 'دينار',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SYP: {
    code: 'SYP',
    name: 'Syrian Pound',
    nameAr: 'الليرة السورية',
    symbol: '£',
    symbolAr: 'ليرة',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  LBP: {
    code: 'LBP',
    name: 'Lebanese Pound',
    nameAr: 'الليرة اللبنانية',
    symbol: 'LBP',
    symbolAr: 'ليرة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  TRY: {
    code: 'TRY',
    name: 'Turkish Lira',
    nameAr: 'الليرة التركية',
    symbol: '₺',
    symbolAr: '₺',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // African Currencies
  ZAR: {
    code: 'ZAR',
    name: 'South African Rand',
    nameAr: 'الراند الجنوب أفريقي',
    symbol: 'R',
    symbolAr: 'راند',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MAD: {
    code: 'MAD',
    name: 'Moroccan Dirham',
    nameAr: 'الدرهم المغربي',
    symbol: 'د.م',
    symbolAr: 'درهم',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  NGN: {
    code: 'NGN',
    name: 'Nigerian Naira',
    nameAr: 'النايرا النيجيرية',
    symbol: '₦',
    symbolAr: 'نايرا',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  KES: {
    code: 'KES',
    name: 'Kenyan Shilling',
    nameAr: 'الشلن الكيني',
    symbol: 'KSh',
    symbolAr: 'شلن',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  GHS: {
    code: 'GHS',
    name: 'Ghanaian Cedi',
    nameAr: 'السيدي الغاني',
    symbol: '₵',
    symbolAr: 'سيدي',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  UGX: {
    code: 'UGX',
    name: 'Ugandan Shilling',
    nameAr: 'الشلن الأوغندي',
    symbol: 'USh',
    symbolAr: 'شلن',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  TZS: {
    code: 'TZS',
    name: 'Tanzanian Shilling',
    nameAr: 'الشلن التنزاني',
    symbol: 'TSh',
    symbolAr: 'شلن',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  ETB: {
    code: 'ETB',
    name: 'Ethiopian Birr',
    nameAr: 'البير الإثيوبي',
    symbol: 'Br',
    symbolAr: 'بير',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // Americas Currencies
  CAD: {
    code: 'CAD',
    name: 'Canadian Dollar',
    nameAr: 'الدولار الكندي',
    symbol: 'C$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  MXN: {
    code: 'MXN',
    name: 'Mexican Peso',
    nameAr: 'البيزو المكسيكي',
    symbol: '$',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BRL: {
    code: 'BRL',
    name: 'Brazilian Real',
    nameAr: 'الريال البرازيلي',
    symbol: 'R$',
    symbolAr: 'ريال',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  ARS: {
    code: 'ARS',
    name: 'Argentine Peso',
    nameAr: 'البيزو الأرجنتيني',
    symbol: '$',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  CLP: {
    code: 'CLP',
    name: 'Chilean Peso',
    nameAr: 'البيزو التشيلي',
    symbol: '$',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  COP: {
    code: 'COP',
    name: 'Colombian Peso',
    nameAr: 'البيزو الكولومبي',
    symbol: '$',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PEN: {
    code: 'PEN',
    name: 'Peruvian Sol',
    nameAr: 'السول البيروفي',
    symbol: 'S/',
    symbolAr: 'سول',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  UYU: {
    code: 'UYU',
    name: 'Uruguayan Peso',
    nameAr: 'البيزو الأوروغواياني',
    symbol: '$U',
    symbolAr: 'بيزو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  BOB: {
    code: 'BOB',
    name: 'Bolivian Boliviano',
    nameAr: 'البوليفيانو البوليفي',
    symbol: 'Bs',
    symbolAr: 'بوليفيانو',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PYG: {
    code: 'PYG',
    name: 'Paraguayan Guarani',
    nameAr: 'الغواراني الباراغواياني',
    symbol: '₲',
    symbolAr: 'غواراني',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  VES: {
    code: 'VES',
    name: 'Venezuelan Bolivar',
    nameAr: 'البوليفار الفنزويلي',
    symbol: 'Bs.S',
    symbolAr: 'بوليفار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // Oceania Currencies
  AUD: {
    code: 'AUD',
    name: 'Australian Dollar',
    nameAr: 'الدولار الأسترالي',
    symbol: 'A$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  NZD: {
    code: 'NZD',
    name: 'New Zealand Dollar',
    nameAr: 'الدولار النيوزيلندي',
    symbol: 'NZ$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  FJD: {
    code: 'FJD',
    name: 'Fijian Dollar',
    nameAr: 'الدولار الفيجي',
    symbol: 'FJ$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  PGK: {
    code: 'PGK',
    name: 'Papua New Guinea Kina',
    nameAr: 'الكينا البابوي',
    symbol: 'K',
    symbolAr: 'كينا',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  WST: {
    code: 'WST',
    name: 'Samoan Tala',
    nameAr: 'التالا الساموي',
    symbol: 'WS$',
    symbolAr: 'تالا',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  TOP: {
    code: 'TOP',
    name: 'Tongan Pa\'anga',
    nameAr: 'البانغا التونغية',
    symbol: 'T$',
    symbolAr: 'بانغا',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  VUV: {
    code: 'VUV',
    name: 'Vanuatu Vatu',
    nameAr: 'الفاتو الفانواتي',
    symbol: 'VT',
    symbolAr: 'فاتو',
    symbolPosition: 'after',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SBD: {
    code: 'SBD',
    name: 'Solomon Islands Dollar',
    nameAr: 'الدولار السولوموني',
    symbol: 'SI$',
    symbolAr: 'دولار',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  // Additional European Currencies
  ISK: {
    code: 'ISK',
    name: 'Icelandic Krona',
    nameAr: 'الكرونة الآيسلندية',
    symbol: 'kr',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 0,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  LTL: {
    code: 'LTL',
    name: 'Lithuanian Litas',
    nameAr: 'الليتاس الليتواني',
    symbol: 'Lt',
    symbolAr: 'ليتاس',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  LVL: {
    code: 'LVL',
    name: 'Latvian Lats',
    nameAr: 'اللاتس اللاتفي',
    symbol: 'Ls',
    symbolAr: 'لاتس',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  EEK: {
    code: 'EEK',
    name: 'Estonian Kroon',
    nameAr: 'الكرون الإستوني',
    symbol: 'kr',
    symbolAr: 'كرون',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SKK: {
    code: 'SKK',
    name: 'Slovak Koruna',
    nameAr: 'الكرونة السلوفاكية',
    symbol: 'Sk',
    symbolAr: 'كرونة',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  },
  SIT: {
    code: 'SIT',
    name: 'Slovenian Tolar',
    nameAr: 'التولار السلوفيني',
    symbol: 'SIT',
    symbolAr: 'تولار',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandsSeparator: ',',
    decimalSeparator: '.'
  }
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('SAR'); // Default to SAR
  const [isLoading, setIsLoading] = useState(true);
  const { user, token } = useAuth();
  const { i18n } = useTranslation();

  // Load currency preference from database when user logs in
  useEffect(() => {
    const loadUserCurrency = async () => {
      if (!user || !token) {
        setIsLoading(false);
        return;
      }

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE_URL}/preferences/currency`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.value && CURRENCIES[data.value]) {
            setSelectedCurrency(data.value);
            console.log('✅ Currency preference loaded from database:', data.value);
          } else {
            console.log('⚠️ No valid currency preference found, using default SAR');
          }
        } else {
          console.error('Failed to load currency preference from database:', response.status);
        }
      } catch (error) {
        console.error('Error loading user currency preference:', error);
        // Fall back to default currency
        setSelectedCurrency('SAR');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserCurrency();
  }, [user?.id, token]); // Only depend on user.id and token, not the entire user object

  const changeCurrency = async (currencyCode) => {
    if (!CURRENCIES[currencyCode]) {
      return;
    }

    setSelectedCurrency(currencyCode);

    // Save to database if user is logged in
    if (user && token) {
      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE_URL}/preferences`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            key: 'currency',
            value: currencyCode
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to save currency preference to database:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData
          });
          // Could implement a retry mechanism or show user notification here
        } else {
          console.log('✅ Currency preference saved successfully to database');
        }
      } catch (error) {
        console.error('Error saving currency preference:', error);
        // Could implement a retry mechanism or show user notification here
      }
    } else {
      // Fallback to localStorage for non-authenticated users
      localStorage.setItem('dish-costing-currency', currencyCode);
    }
  };

  const getCurrentCurrency = () => {
    return CURRENCIES[selectedCurrency] || CURRENCIES.SAR;
  };

  const formatCurrency = (amount) => {
    const currency = getCurrentCurrency();
    const num = parseFloat(amount);
    
    // Get the appropriate symbol based on current language
    const symbol = i18n.language === 'ar' ? currency.symbolAr : currency.symbol;
    
    if (isNaN(num)) {
      return currency.symbolPosition === 'before' 
        ? `${symbol} 0.00` 
        : `0.00 ${symbol}`;
    }
    
    // Format with thousands separators and appropriate decimal places
    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: currency.decimalPlaces,
      maximumFractionDigits: currency.decimalPlaces
    });
    
    return currency.symbolPosition === 'before' 
      ? `${symbol} ${formatted}` 
      : `${formatted} ${symbol}`;
  };

  const value = {
    selectedCurrency,
    changeCurrency,
    getCurrentCurrency,
    formatCurrency,
    currencies: CURRENCIES,
    isLoading
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
